import { useState, useEffect } from "react";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import { PaymentInfo, PAYMENT_STATUS } from "@/types/payment.type";
import { formatDate } from "@/common/helpers/date.helpers";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { ModalAddBillingB2B } from "../modals/ModalAddBillingB2B";
import { useParams } from "react-router-dom";
import { syncPaymentsB2B, getPaymentsB2B } from "@/common/services/company-b2b.api";


const PAYMENT_HEADERS = {
  description: "Descrição",
  value: "Valor",
  dueDate: "Vencimento",
  status: "Status",
};

export function PaymentsB2B() {
  const { id } = useParams<{ id: string }>();

  const [payments, setPayments] = useState<PaymentInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openBillingModal, setOpenBillingModal] = useState(false);
  const { notifySuccess, notifyError } = useToastContext();

  const loadPayments = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const result = await getPaymentsB2B(id);

      if (result.ok && result.data) {
        setPayments(result.data as unknown as PaymentInfo[]);
      } else {
        notifyError("Erro ao carregar pagamentos");
      }
    } catch (error) {
      console.error("Erro ao carregar pagamentos:", error);
      notifyError("Erro ao carregar pagamentos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddBilling = () => {
    setOpenBillingModal(true);
  };

  const handleSyncAsaas = async () => {
    if (!id) {
      notifyError("ID da empresa não encontrado");
      return;
    }

    setIsLoading(true);
    try {
      const result = await syncPaymentsB2B(id);

      if (result.ok) {
        notifySuccess("Pagamentos sincronizados com sucesso!");
        await loadPayments();
      } else {
        notifyError("Erro ao sincronizar pagamentos");
      }
    } catch (error) {
      console.error("Erro ao sincronizar:", error);
      notifyError("Erro ao sincronizar pagamentos com Asaas");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const statusMap = {
      [PAYMENT_STATUS.PENDING]: "Pendente",
      [PAYMENT_STATUS.RECEIVED]: "Recebido",
      [PAYMENT_STATUS.OVERDUE]: "Vencido",
      [PAYMENT_STATUS.Inativo]: "Inativo",
    };
    return statusMap[status] || status;
  };

  const getStatusStyle = (status: string) => {
    const styles = {
      [PAYMENT_STATUS.PENDING]: "text-yellow-600 font-semibold",
      [PAYMENT_STATUS.RECEIVED]: "text-green-600 font-semibold",
      [PAYMENT_STATUS.OVERDUE]: "text-red-600 font-semibold",
      [PAYMENT_STATUS.Inativo]: "text-gray-400",
    };
    return styles[status] || "";
  };

  return (
    <div className="tabs-border-line">
      <section className="tabs-item-style">
        {/* Header com botões */}
        <div className="flex items-center gap-3 mb-6">
          <ButtonWithLoader
            onClick={handleSyncAsaas}
            isLoading={isLoading}
          >
            Sincronizar com Asaas
          </ButtonWithLoader>

          <ButtonWithLoader
            onClick={handleAddBilling}
          >
            Adicionar Cobrança
          </ButtonWithLoader>
        </div>

        {/* Tabela de pagamentos */}
        <GenericTable
          headers={PAYMENT_HEADERS}
          data={payments as unknown as Record<string, unknown>[]}
          renders={{
            description: (desc: unknown) => (
              <span className="font-medium">{String(desc) || "-"}</span>
            ),
            value: (value: unknown) => (
              <span className="font-semibold">
                {formatCurrency(Number(value) || 0)}
              </span>
            ),
            dueDate: (date: unknown) => (
              <span>{date ? formatDate(date as string) : "-"}</span>
            ),
            status: (_: unknown, row: unknown) => {
              const payment = row as PaymentInfo;
              return (
                <span className={getStatusStyle(payment.status || '')}>
                  {getStatusLabel(payment.status || '')}
                </span>
              );
            },
          }}
          entity="payments-b2b"
        />

        {payments.length === 0 && !isLoading && (
          <div className="text-center py-8 text-gray-500">
            Nenhum pagamento encontrado
          </div>
        )}

        {/* Modal de cobrança */}
        {openBillingModal && (
          <ModalAddBillingB2B
            companyId={id}
            onClose={() => setOpenBillingModal(false)}
            onSuccess={() => {
              setOpenBillingModal(false);
              loadPayments();
            }}
          />
        )}
      </section>
    </div>
  );
}